import { useCallback } from "react";

import { useDeepResearchSocketContext } from "@/pages/Private/pages/ProfileTracker/providers/DeepResearchSocketContext";

import { useSeedDeepResearchRuns } from "./useSeedDeepResearchRuns";

import type {
	DeepResearchFailure,
	DeepResearchRunView,
} from "@/pages/Private/pages/ProfileTracker/providers/deepResearchRunState";

interface UseDeepResearchSocketReturn {
	isConnected: boolean;
	isPending: boolean;
	/** Queued or running, and since when (PREC-4052). */
	run: DeepResearchRunView | null;
	/** The last run failed, and whether a retry can succeed (PREC-4052). */
	failure: DeepResearchFailure | null;
	/** Mark a run as scheduled, remembering which research the lead showed at that moment. */
	startPending: (baselineResponseId?: number | null) => void;
	/** Hand the run the research the lead currently carries (PREC-4024). */
	settlePending: (responseId?: number | null) => void;
	clearError: () => void;
}

/**
 * One prospect's deep research, for a surface that shows it on its own (the lead panel). Asks the
 * server where the research stands, so a reload, the extension or a run past ten minutes shows
 * the same state (PREC-4218, PREC-4052).
 */
export const useDeepResearchSocket = (prospectId?: number): UseDeepResearchSocketReturn => {
	const context = useDeepResearchSocketContext();
	const { isConnected, isPending, getRun, getFailure, startPending, settlePending, clearError } =
		context;

	useSeedDeepResearchRuns(prospectId ? [prospectId] : [], context);

	const startPendingForCurrent = useCallback(
		(baselineResponseId?: number | null) => {
			startPending(prospectId, baselineResponseId);
		},
		[prospectId, startPending]
	);

	const settlePendingForCurrent = useCallback(
		(responseId?: number | null) => {
			settlePending(prospectId, responseId);
		},
		[prospectId, settlePending]
	);

	const clearErrorForCurrent = useCallback(() => {
		clearError(prospectId);
	}, [prospectId, clearError]);

	return {
		isConnected,
		isPending: isPending(prospectId),
		run: getRun(prospectId),
		failure: getFailure(prospectId),
		startPending: startPendingForCurrent,
		settlePending: settlePendingForCurrent,
		clearError: clearErrorForCurrent,
	};
};
